import { apiFetch } from '.'
import { toCamelCase, toSnakeCase } from '../object-utils'
import ApiExtension from './api-extension'
import { removeFileExtension } from '../string-utils'
import FileNotFoundError from './file-not-found-error'

/**
 * @typedef {Object} CostMeta
 * @property {string} currency_symbol
 * @property {number} cost_per_unit
 * @property {number} min_cost
 * @property {number} fixed_cost
 */

/**
 * @typedef {Object} Cost
 * @property {Record<string, { amount: number; sufficient: boolean }>} totals
 * @property {CostMeta} meta:
 * @property {number?} detection_deduction
 * @property {number?} upload_deduction
 */

export default class PlantDetection extends ApiExtension {
  getPlantDetections(projectId) {
    return this.fetch('project_plant_detections', {
      project_id: projectId,
    }).then(data => toCamelCase(data))
  }

  getPlantDetection(projectId, plantDetectionId) {
    return this.fetch('project_plant_detection', {
      project_id: projectId,
      id: plantDetectionId,
    }).then(data => toCamelCase(data))
  }

  createPlantDetection(projectId, plantDetection) {
    return this.fetch(
      'project_plant_detections',
      { project_id: projectId },
      {
        method: 'POST',
        body: toSnakeCase({
          plantDetection,
        }),
      }
    ).then(({ id }) => id)
  }

  savePlantDetection(projectId, plantDetectionId, plantDetection) {
    return this.fetch(
      'project_plant_detection',
      { project_id: projectId, id: plantDetectionId },
      {
        method: 'PUT',
        body: {
          plantDetection,
        },
      }
    )
  }

  deletePlantDetection(projectId, plantDetectionId) {
    return this.fetch(
      'project_plant_detection',
      { project_id: projectId, id: plantDetectionId },
      {
        method: 'DELETE',
      }
    )
  }

  getPlantDetectionModels(projectId) {
    return this.fetch('project_plant_detection_models', {
      project_id: projectId,
    }).then(data => toCamelCase(data))
  }

  getCustomPlantDetectionModels() {
    return this.fetch('custom_plant_detection_models').then(toCamelCase)
  }

  createPlantDetectionModel(projectId, model) {
    return this.fetch(
      'project_plant_detection_models',
      { project_id: projectId },
      { method: 'POST', body: { model: model } }
    )
  }

  saveProjectPlantDetectionModel(projectId, model) {
    return this.fetch(
      'project_plant_detection_model',
      { project_id: projectId, id: model.id },
      { method: 'PUT', body: { model: model } }
    )
  }

  updatePlantDetectionModel(modelId, model) {
    return this.fetch(
      'plant_detection_model',
      { id: modelId },
      { method: 'PUT', body: { model: model } }
    )
  }

  listPlantDetections(
    projectIdentifier,
    includeUrls = false,
    jobFilterFn = filterDetectPlants
  ) {
    return this.apiBase.job.getJobs(projectIdentifier).then(jobs => {
      const detectionJobs = jobs.filter(
        j => jobFilterFn(j) && j.status === 'completed'
      )

      return includeUrls
        ? Promise.all(
            detectionJobs.map(j =>
              this.apiBase.job.getJob(projectIdentifier, j.id)
            )
          ).then(results =>
            results.map(({ urls }, i) => ({ ...detectionJobs[i], urls }))
          )
        : detectionJobs
    })
  }

  getDetection(projectIdentifier, id) {
    return this.apiBase.job.getJob(projectIdentifier, id).then(({ urls }) => {
      return Promise.all(
        [findExt('.shp'), findExt('.dbf')].map(url => window.fetch(url))
      )
        .then(function (responses) {
          if (responses.some(r => r.status !== 200)) {
            throw new Error(
              `Unexpected HTTP responses: ${responses(r => r.status).join(
                ', '
              )}`
            )
          }

          return Promise.all(
            responses.map(r =>
              r.arrayBuffer().then(arrayBuffer => ({
                lastModified: r.headers.get('last-modified'),
                arrayBuffer,
              }))
            )
          )
        })
        .then(function ([
          { lastModified, arrayBuffer: shapeFile },
          { arrayBuffer: dbfFile },
        ]) {
          return {
            id: id,
            date: new Date(lastModified),
            shapeFile,
            dbfFile,
          }
        })

      function findExt(ext) {
        const url = urls.find(u => new URL(u).pathname.endsWith(ext))
        if (url) {
          return url
        }

        throw new Error(`No ${ext} in results.`)
      }
    })
  }

  getDetectionResult(projectIdentifier, detectionId, filename, fetchOptions) {
    filename = this.apiBase.job.urlEncodeFilename(filename)

    return this.apiBase.job
      .getJob(projectIdentifier, detectionId)
      .then(({ urls }) => urls.find(u => u.indexOf(`/${filename}`) >= 0))
      .then(url => {
        if (url) {
          return apiFetch(url, fetchOptions)
        } else {
          throw new FileNotFoundError(`No result file named ${filename}`)
        }
      })
  }

  getDetectionSummary(projectIdentifier, detectionId) {
    return this.getDetectionResult(
      projectIdentifier,
      detectionId,
      'summary.json'
    )
  }

  getImageDetectionResult(projectIdentifier, detectionId, imageFilename) {
    const filenameWithoutExt = imageFilename.substr(
      0,
      imageFilename.lastIndexOf('.')
    )

    return this.getDetectionResult(
      projectIdentifier,
      detectionId,
      `${filenameWithoutExt}.geojson`
    )
  }

  /**
   *
   * @param {number} projectId
   * @param {string} projectIdentifier
   * @param {number} plantDetectionId
   * @param {import('../components/plant-ai/context/state-utils').DetectionBackend} backend
   * @param {import('../components/plant-ai/context/state-utils').Annotation[]} annotations
   * @param {import('../components/plant-ai/context/state-utils').TrainingParams} trainingParams
   * @param {import('../components/plant-ai/context/state-utils').DetectionParams} detectionParams
   * @param {import('../components/plant-ai/detection-utils').ProgressCallback?} progressCallback
   * @param {number?} priority
   * @returns
   */
  detectPlants(
    projectId,
    projectIdentifier,
    plantDetectionId,
    backend,
    annotations,
    params,
    progressCallback = undefined,
    priority = undefined
  ) {
    const filenames = params?.filenames
    const prefix = backend === 'plantai' ? 'plantai-' : ''

    const annotationsAttachments = !filenames
      ? getAnnotationAttachments(annotations)
      : null

    return this.apiBase.job
      .fetchJobResult(
        projectIdentifier,
        `${prefix}detect_plants`,
        { ...params, detection_id: plantDetectionId },
        {
          attachments: annotationsAttachments,
          // Use plant detection id if available, otherwise its an annotation detection
          ...(plantDetectionId
            ? {
                creationRoute: 'detect_project_plant_detection',
                creationRouteParams: {
                  project_id: projectId,
                  id: plantDetectionId,
                },
                creationParamsWrapper: 'job',
              }
            : {
                creationRoute: 'detect_annotations_project_plant_detections',
                creationRouteParams: {
                  project_id: projectId,
                },
                creationParamsWrapper: 'job',
              }),
          progressCallback,
          priority,
        }
      )
      .then(data => toCamelCase(data))
  }

  /**
   *
   * @param {number} projectId
   * @param {string} projectIdentifier
   * @param {number} plantDetectionId
   * @param {import('../components/plant-ai/context/state-utils').DetectionBackend} backend
   * @param {import('../components/plant-ai/context/state-utils').Annotation[]} annotations
   * @param {import('../components/plant-ai/context/state-utils').TrainingParams} trainingParams
   * @param {import('../components/plant-ai/context/state-utils').DetectionParams} detectionParams
   * @param {import('../components/plant-ai/detection-utils').ProgressCallback?} progressCallback
   * @param {number?} priority
   * @returns
   */
  trainAndDetect(
    projectId,
    projectIdentifier,
    plantDetectionId,
    backend,
    annotations,
    trainingParams,
    detectionParams,
    progressCallback = undefined,
    priority = undefined
  ) {
    const filenames = trainingParams?.filenames
    const prefix = backend === 'plantai' ? 'plantai-' : ''

    const annotationsAttachments = getAnnotationAttachments(
      annotations,
      filenames
    )

    return this.apiBase.job
      .fetchJobResult(
        projectIdentifier,
        `${prefix}train_and_detect`,
        {
          trainingParams,
          detectionParams,
          detection_id: plantDetectionId,
        },
        {
          attachments: annotationsAttachments,
          creationRoute: 'detect_project_plant_detection',
          creationRouteParams: {
            project_id: projectId,
            id: plantDetectionId,
          },
          creationParamsWrapper: 'job',
          progressCallback,
          priority,
        }
      )
      .then(data => toCamelCase(data))
  }

  filterScore(projectIdentifier, plantCountJobId, scoreThreshold) {
    return this.apiBase.job.fetchJobResult(
      projectIdentifier,
      'exports/filter_scores',
      { plantCountJobId, scoreThreshold }
    )
  }

  publish(projectId, detectionId, jobId) {
    return this.fetch(
      'project_plant_detection_result_publish',
      {
        project_id: projectId,
        plant_detection_id: detectionId,
        result_id: jobId,
      },
      { method: 'POST' }
    )
  }

  /**
   *
   * @param {number} projectId
   * @param {*} params
   * @returns {Promise<Cost>}
   */
  getCost(projectId, params) {
    return this.fetch(
      'project_cost',
      {
        project_id: projectId,
        id: 'plant_detection',
      },
      null,
      { queryParams: params }
    )
  }

  purchase(projectId, params) {
    return this.fetch(
      'purchase_project_purchase',
      {
        project_id: projectId,
        id: 'plant_detection',
      },
      { method: 'POST', body: params }
    )
  }

  requestAccess() {
    return this.fetch(
      'user_requests',
      {},
      { method: 'POST', body: { requestType: 'plant_ai' } }
    )
  }
}

function filterDetectPlants(job) {
  return job.operation === 'detect_plants'
}

function getAnnotationAttachments(annotations, filenames) {
  // Annotations is a list of feature collections
  // if filenames is not defined then just get the first annotation
  // otherwise filter the annotations
  if (filenames && Array.isArray(filenames)) {
    const fileAnnotations = annotations.filter(annotation =>
      filenames.includes(annotation.properties.image)
    )

    return fileAnnotations.map(annotationsAttachment)
  } else {
    const strippedAnnotation = annotations[0]
    const annotationsAttachment = new Blob(
      [JSON.stringify(strippedAnnotation, sortJsonKeys)],
      {
        type: 'application/json',
      }
    )
    annotationsAttachment.filename = 'annotations.json'

    return [annotationsAttachment]
  }
}

function annotationsAttachment(annotations) {
  const annotationsAttachment = new Blob(
    [JSON.stringify(annotations, sortJsonKeys)],
    {
      type: 'application/json',
    }
  )

  annotationsAttachment.filename =
    removeFileExtension(annotations?.properties?.image) + '.json'

  return annotationsAttachment
}

/**
 * Replacer function for `JSON.stringify` to ensure keys are always in same order (alphabetical),
 * which in turn is required to make hash of the stringified JSON stable.
 *
 * More or less copied from https://stackoverflow.com/a/43636793
 */
function sortJsonKeys(key, value) {
  return value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value
}
